import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
    FileText, ArrowLeft, Loader2, AlertTriangle, Save, Users, CheckCircle2, XCircle,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Owner-only: edit the active contract text + see who has signed it.
 *
 * Two-pane view:
 *   - Left/main: textarea with the contract body. "Save" bumps version
 *     and invalidates all prior signatures.
 *   - Right/below: table of coaches with their signature status against
 *     the CURRENT active version. ✓ green / ⚠ amber if older / ✗ not signed.
 */
export default function ContractEditor() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [contract, setContract] = useState(null);
    const [signatures, setSignatures] = useState([]);
    const [busy, setBusy] = useState(true);
    const [title, setTitle] = useState("");
    const [body, setBody] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (loading) return;
        if (!user || user.role !== "owner") {
            navigate("/coach/coaches", { replace: true });
        }
    }, [loading, user, navigate]);

    const load = async () => {
        setBusy(true);
        try {
            const [c, s] = await Promise.all([
                axios.get(`${API}/coach/contract/active`, { withCredentials: true }),
                axios.get(`${API}/coach/contracts/signatures`, { withCredentials: true }),
            ]);
            setContract(c.data.contract);
            setTitle(c.data.contract.title);
            setBody(c.data.contract.body_markdown);
            setSignatures(s.data.signatures || []);
        } catch (e) {
            toast.error("Couldn't load contract", { description: e?.response?.data?.detail });
        } finally {
            setBusy(false);
        }
    };

    useEffect(() => { if (!loading && user?.role === "owner") load(); }, [loading, user]);

    const save = async (e) => {
        e.preventDefault();
        if (!title.trim() || !body.trim()) return;
        if (!window.confirm(
            "Saving will bump the contract version and invalidate ALL prior coach signatures. " +
            "Every coach will need to re-sign before continuing. Proceed?"
        )) return;
        setSaving(true);
        try {
            await axios.put(
                `${API}/coach/contract`,
                { title: title.trim(), body_markdown: body },
                { withCredentials: true },
            );
            toast.success("Contract updated", { description: "All coaches will be prompted to re-sign." });
            await load();
        } catch (e) {
            toast.error("Couldn't save", { description: e?.response?.data?.detail });
        } finally {
            setSaving(false);
        }
    };

    if (loading || busy || !contract) {
        return (
            <div className="min-h-screen bg-black flex items-center justify-center">
                <Loader2 className="w-8 h-8 animate-spin text-brand-red" />
            </div>
        );
    }

    const signed = signatures.filter((s) => s.is_current);
    const pending = signatures.filter((s) => !s.is_current);
    const dirty = title !== contract.title || body !== contract.body_markdown;

    return (
        <div className="min-h-screen bg-black text-white">
            <div className="max-w-6xl mx-auto px-6 md:px-10 py-12">
                <div className="flex items-center gap-3 mb-4">
                    <Link
                        to="/coach/coaches"
                        data-testid="contract-editor-back"
                        className="text-zinc-400 hover:text-white inline-flex items-center gap-2 text-sm"
                    >
                        <ArrowLeft className="w-4 h-4" /> Coaches
                    </Link>
                </div>
                <div className="text-[11px] tracking-[0.3em] uppercase text-brand-red mb-4 inline-flex items-center gap-3">
                    <span className="inline-block w-10 h-px bg-brand-red" />
                    Owner console · Contract editor
                </div>
                <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
                    <div>
                        <h1 className="font-display text-4xl md:text-5xl tracking-wider leading-[0.92] text-white">
                            EDIT CONTRACT.
                        </h1>
                        <p className="mt-3 text-zinc-400 text-sm">
                            Currently active: <span className="text-white">version {contract.version}</span>. Last updated {new Date(contract.updated_at).toLocaleString()}.
                        </p>
                    </div>
                </div>

                {/* Editor */}
                <form onSubmit={save} className="border border-zinc-900 bg-zinc-950/40 rounded-2xl p-6 mb-10">
                    <label className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 mb-1.5 block">Contract title</label>
                    <input
                        type="text"
                        data-testid="contract-editor-title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        required
                        className="w-full bg-zinc-900 border border-zinc-800 text-white text-lg px-3 py-2 rounded focus:outline-none focus:border-brand-red mb-5"
                    />

                    <label className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 mb-1.5 block">Contract body (Markdown supported)</label>
                    <textarea
                        data-testid="contract-editor-body"
                        value={body}
                        onChange={(e) => setBody(e.target.value)}
                        required
                        rows={20}
                        className="w-full bg-zinc-900 border border-zinc-800 text-white text-sm px-3 py-3 rounded focus:outline-none focus:border-brand-red font-mono leading-relaxed"
                    />

                    {dirty && (
                        <div className="mt-4 px-4 py-3 border border-amber-700/50 bg-amber-950/30 rounded inline-flex items-start gap-2.5">
                            <AlertTriangle className="w-4 h-4 text-amber-300 flex-shrink-0 mt-0.5" />
                            <div className="text-xs text-amber-200">
                                Saving will bump to <span className="text-white">version {contract.version + 1}</span> and require all {signatures.length} coach{signatures.length === 1 ? "" : "es"} to re-sign before continuing.
                            </div>
                        </div>
                    )}

                    <div className="mt-5 flex items-center justify-end gap-3">
                        <button
                            type="button"
                            data-testid="contract-editor-reset"
                            onClick={() => { setTitle(contract.title); setBody(contract.body_markdown); }}
                            disabled={!dirty || saving}
                            className="text-zinc-400 hover:text-white text-xs tracking-wider uppercase px-3 py-2 disabled:opacity-30"
                        >
                            Reset
                        </button>
                        <button
                            type="submit"
                            data-testid="contract-editor-save"
                            disabled={!dirty || saving || !title.trim() || !body.trim()}
                            className="btn-tactical inline-flex items-center gap-2 disabled:opacity-50"
                        >
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                            {saving ? "Saving…" : "Save & bump version"}
                        </button>
                    </div>
                </form>

                {/* Signatures table */}
                <section>
                    <h2 className="text-xs tracking-[0.3em] uppercase text-zinc-300 mb-3 inline-flex items-center gap-2">
                        <Users className="w-4 h-4" /> Signature status · {signed.length} signed · {pending.length} pending
                    </h2>
                    {signatures.length === 0 ? (
                        <div className="border border-zinc-900 bg-zinc-950/40 rounded-2xl px-6 py-12 text-center text-zinc-500">
                            No coaches yet.
                        </div>
                    ) : (
                        <div className="border border-zinc-900 bg-zinc-950/40 rounded-2xl overflow-hidden">
                            <table className="w-full text-sm">
                                <thead className="bg-zinc-900/50 text-[10px] tracking-wider uppercase text-zinc-300">
                                    <tr>
                                        <th className="text-left px-4 py-3">Coach</th>
                                        <th className="text-left px-4 py-3">Role</th>
                                        <th className="text-left px-4 py-3">Status</th>
                                        <th className="text-left px-4 py-3">Signed version</th>
                                        <th className="text-left px-4 py-3">Signed at</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {signatures.map((s) => (
                                        <tr key={s.user_id} data-testid={`contract-signature-${s.user_id}`} className="border-t border-zinc-900">
                                            <td className="px-4 py-3 text-white">{s.name}</td>
                                            <td className="px-4 py-3 text-zinc-300 text-xs tracking-wider uppercase">{s.role}</td>
                                            <td className="px-4 py-3">
                                                {s.is_current ? (
                                                    <span className="inline-flex items-center gap-1.5 text-emerald-300 text-xs tracking-wider uppercase">
                                                        <CheckCircle2 className="w-4 h-4" /> Signed
                                                    </span>
                                                ) : s.signed_version ? (
                                                    <span className="inline-flex items-center gap-1.5 text-amber-300 text-xs tracking-wider uppercase">
                                                        <AlertTriangle className="w-4 h-4" /> Outdated · re-sign needed
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1.5 text-rose-400 text-xs tracking-wider uppercase">
                                                        <XCircle className="w-4 h-4" /> Not signed
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3 text-zinc-300">{s.signed_version ? `v${s.signed_version}` : "—"}</td>
                                            <td className="px-4 py-3 text-zinc-300">{s.signed_at ? new Date(s.signed_at).toLocaleString() : "—"}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
}
